/* eslint-disable import/prefer-default-export */
import { useState, useEffect, useMemo } from 'react'
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { ProductFilterKeys } from '../../types/product'
import {
  CollectionByHandlerQuery,
  CollectionByHandlerQueryVariables,
  Filter,
  ProductCollectionSortKeys,
} from '../../types/storefront'
import collectionHandlerQuery from '../api/graphql/collectionHandlerQuery.gql'
import { CollectionSortList } from '../utils/constants'
import { getCursors } from '../utils/helpers'
import { routeHistory } from '../utils/scroll-handler'
import {
  parseQueryToFilter,
  parseQueryToFilterParams,
} from '../utils/query-parser'

export function useGetCollectionsByHandler(
  initialState: CollectionByHandlerQuery['collection']
) {
  const router = useRouter()
  const client = useApolloClient()
  const routerQuery = useMemo(() => {
    const { handle, sort_by: sortBy } = router.query as {
      handle: string[]
      sort_by: string
    }
    return { handle, sortBy }
  }, [router.query])
  const [staticData, setStatic] = useState<CollectionByHandlerQuery | null>(
    null
  )
  const [collection] =
    (typeof routerQuery.handle === 'string'
      ? [routerQuery.handle]
      : routerQuery.handle) || []

  const [variables, setVariables] = useState<CollectionByHandlerQueryVariables>(
    {
      handle: collection,
      sortKey: ProductCollectionSortKeys.CollectionDefault,
      reverse: false,
      first: 24,
    }
  )

  // Query in shopify
  const [getCollection, query] = useLazyQuery<
    CollectionByHandlerQuery,
    CollectionByHandlerQueryVariables
  >(collectionHandlerQuery, {
    notifyOnNetworkStatusChange: true,
  })

  const data = useMemo(() => {
    const collectionData = query.data?.collection
    // const collectionData =
    //   !variables.filters &&
    //   variables.sortKey === ProductCollectionSortKeys.CollectionDefault
    //     ? staticData?.collection
    //     : query.data?.collection
    // @ts-ignore
    return {
      collection: collectionData,
      products: collectionData?.products,
    }
  }, [query])

  // Collection filters
  /**
   * filtersmap
   * { key(lower case): Filter }
   */
  const filtersMap = useMemo(
    () =>
      data?.products?.filters
        .filter((i) => i.label !== 'Availability')
        .reduce<Record<string, Filter>>((acc, i) => {
          acc[i.label.replace(/\s/, '_').toLowerCase()] = i
          return acc
        }, {}),
    [data.products]
  )

  const collectionCount = useMemo(
    () =>
      data?.products?.filters
        .find((i) => i.label === 'Availability')
        ?.values.find((i) => i.label === 'In stock')?.count || 0,
    [data]
  )

  const fetchGqlMore = () => {
    if (!data?.products?.pageInfo.hasNextPage) return

    const cursor = getCursors(data.products.edges)
    if (!cursor) return

    query.fetchMore?.({
      variables: {
        ...variables,
        after: cursor,
      },
    })
  }

  const fetchStaticMore = async () => {
    const cursor = getCursors(data?.products?.edges)
    if (!cursor) return

    const res = await fetch(
      `/_next/data/${process.env.APP_RELEASE}/collection/${collection}/${cursor}.json`
    )
    const json = await res.json()
    client.writeQuery({
      query: collectionHandlerQuery,
      variables: { ...variables, after: cursor },
      data: { collection: json.pageProps },
    })
  }

  const fetchMore = useMemo(
    () =>
      !variables.filters &&
      variables.sortKey === ProductCollectionSortKeys.CollectionDefault
        ? fetchStaticMore
        : fetchGqlMore,
    [data, variables]
  )

  /**
   * Selected sort filter
   */
  const sortItem = useMemo(() => {
    if (!routerQuery.sortBy) return CollectionSortList[0]
    return CollectionSortList.find((i) => i.searchValue === routerQuery.sortBy)
  }, [routerQuery])
  /**
   * Product filter map in router
   */
  const productFilters = useMemo(() => {
    const params = parseQueryToFilter(router.query)
      .map((i) => JSON.parse(i.input))
      .flat()
    const extra = parseQueryToFilterParams(router.query)
    return params.concat(extra)
  }, [router.query])
  const updateVars = () => {
    const vars: CollectionByHandlerQueryVariables = {
      ...variables,
      handle: collection,
    }

    // Set sort options
    const { value, reverse } = sortItem || {}
    if (value) vars.sortKey = value
    vars.reverse = reverse || false

    vars.filters = productFilters
    if (!productFilters.length) vars.filters = undefined

    setVariables({ ...vars })
  }

  /**
   * Check is static requet
   * if not filters or sortkey, request data from static
   */
  const isStaticRequest = useMemo(() => {
    if (!process.browser) return true

    const s = window.location.search
    return (
      !Object.values(ProductFilterKeys).some((v) => s.includes(v)) &&
      (s.indexOf('sort_by') < 0 || s.indexOf('sort_by=manual') >= 0)
    )
  }, [router.query])
  /**
   * static props render dispatcher
   */
  const dispatchInitialQuery = (force?: boolean) => {
    if (!variables.handle) return

    const prev = query.previousData
    if (force || !prev || prev.collection?.handle !== initialState?.handle) {
      client.writeQuery({
        query: collectionHandlerQuery,
        variables,
        data: { collection: initialState },
        broadcast: true,
        overwrite: true,
      })
    }
  }

  useEffect(() => {
    if (!variables.handle || query.loading) return
    if (isStaticRequest) {
      if (!router.isFallback && initialState) {
        const isBack =
          routeHistory[routeHistory.length - 1]?.pathname.indexOf(
            '/collections'
          ) < 0
        if (!isBack) dispatchInitialQuery()
        if (!query.called) getCollection({ variables })
        return
      }
    }

    if (
      !isStaticRequest &&
      !variables.filters &&
      variables.sortKey === ProductCollectionSortKeys.CollectionDefault
    ) {
      return
    }
    // Get data from network query
    if (query.refetch && typeof query.data !== 'undefined') {
      query.refetch(variables)
    } else {
      getCollection({ variables })
    }
  }, [variables, router.isFallback])

  // Initial
  useEffect(() => {
    // dispatchInitialQuery()

    updateVars()
  }, [JSON.stringify(sortItem), JSON.stringify(productFilters)])

  return {
    sortItem,
    dispatchInitialQuery,
    getCollection,
    variables,
    query,
    fetchMore,
    data,
    filtersMap,
    setVariables,
    staticData,
    setStatic,
    collectionCount,
    isStaticRequest,
  }
}

export function useCollectionQuery(options: {
  variables: CollectionByHandlerQueryVariables
}) {
  const query = useQuery<
    CollectionByHandlerQuery,
    CollectionByHandlerQueryVariables
  >(collectionHandlerQuery, {
    variables: options.variables,
  })

  return { ...query }
}
